// src/pages/PlanificacionVisitas.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonContent, IonCard, IonCardHeader, IonCardTitle, IonCardContent,
  IonList, IonItem, IonLabel, IonBadge, IonModal, IonInput, IonTextarea,
  IonSelect, IonSelectOption, IonToast, IonSpinner, IonText, IonDatetime,
  IonProgressBar
} from "@ionic/react";
import { chevronBack, chevronForward, add, pencil, checkmarkCircle, sparkles, trophy } from "ionicons/icons";
import { IonSegment, IonSegmentButton } from "@ionic/react";
import login from "../services/login";
import "./PlanificacionVisitas.css";

import {
  listarVisitas,
  resumenVisitas,
  crearVisita,
  editarVisita,
  listarFiscalesAsignados,
  recomendacionRuta,
  avanceFiscal,
  justificarVisita,
  listarCausasIncumplimiento,
  IVisita,
  IResumenResponse,
  IAvanceResponse,
  ICausaIncumplimiento,
  EstadoVisita,
  Prioridad,
} from "../services/visitas";
import { fetchPuntoServicio, IfetchPuntoServicio } from "../services/PedidoDeProducto/pedido.producto.services";
import Header from "src/components/Header";

type ModoForm = "crear" | "editar";

function esJefeOperaciones(data: any): boolean {
  // Extraer posibles campos de cargo/roles desde distintos payloads
  const posibles = [
    data?.cargo_nombre, data?.cargo, data?.rol, data?.puesto,
    data?.datos?.cargo_nombre, data?.datos?.cargo, data?.datos?.rol,
    data?.datos?.emp_cargo, data?.emp_cargo,
    ...(Array.isArray(data?.roles) ? data.roles : []),
    ...(Array.isArray(data?.datos?.roles) ? data.datos.roles : []),
  ]
    .filter(Boolean)
    .map((x: any) => String(x).toUpperCase());

  // Palabras clave amplias
  const incluye = (txt: string, ...parts: string[]) =>
    parts.every(p => txt.includes(p));

  for (const t of posibles) {
    if (incluye(t, "JEFE", "OPERAC")) return true;        // JEFE DE OPERACIONES / JEFE OPERACIONES
    if (incluye(t, "JEFATURA")) return true;              // JEFATURA
  }
  return false;
}

function esGerenteOperaciones(data: any): boolean {
  const posibles = [
    data?.cargo_nombre, data?.cargo, data?.rol, data?.puesto,
    data?.datos?.cargo_nombre, data?.datos?.cargo, data?.datos?.rol,
    data?.datos?.emp_cargo, data?.emp_cargo,
    ...(Array.isArray(data?.roles) ? data.roles : []),
    ...(Array.isArray(data?.datos?.roles) ? data.datos.roles : []),
  ]
    .filter(Boolean)
    .map((x: any) => String(x).toUpperCase());

  const incluye = (txt: string, ...parts: string[]) =>
    parts.every(p => txt.includes(p));

  for (const t of posibles) {
    if (incluye(t, "GERENTE", "OPERAC")) return true;     // GERENTE DE OPERACIONES
    if (incluye(t, "UNIDADES", "ESPECIALIZADAS")) return true; // GERENTE ... UNIDADES ESPECIALIZADAS
  }
  return false;
}

/* Helpers fechas */
const fmtISO = (d: Date) => d.toISOString().slice(0, 10);
const monday = (base = new Date()) => {
  const x = new Date(base);
  const wd = x.getDay();
  const delta = wd === 0 ? -6 : 1 - wd;
  x.setDate(x.getDate() + delta);
  x.setHours(0, 0, 0, 0);
  return x;
};
const weekdays = (m: Date) =>
  Array.from({ length: 6 }, (_, i) => {   // lunes..sabado (incluye sabado)
    const d = new Date(m);
    d.setDate(m.getDate() + i);
    return d;
  });
const monthOf = (d: Date) => d.getMonth() + 1;
const yearOf = (d: Date) => d.getFullYear();

// Cumplimiento por tiempo de una visita completada: 100% = estuvo lo planificado.
const colorCumplimiento = (pct: number | null | undefined) =>
  pct == null ? "medium" : pct >= 100 ? "success" : pct >= 50 ? "warning" : "danger";
const textoCumplimientoVisita = (v: IVisita) => {
  if (v.medicion_permanencia === "SIN_SALIDA") return "Sin marcación de salida · 0%";
  if (v.medicion_permanencia === "SIN_MARCACION") return "Sin marcación en el PS · 0%";
  if (v.pct_cumplimiento == null) return null;
  return `Cumplida ${v.pct_cumplimiento}% · ${v.minutos_acreditados ?? 0}/${v.cantidad_horas} min`;
};

const estadoColor = (e: EstadoVisita) =>
  e === "COMPLETADA" ? "success" : e === "VENCIDA" ? "danger" : "medium";
const prioridadColor = (p?: Prioridad | string) => {
  switch ((p || "MEDIA").toUpperCase()) {
    case "ALTA":
      return "danger";
    case "BAJA":
      return "medium";
    default:
      return "warning";
  }
};
const esPrioridadAlta = (p?: Prioridad | string | null) =>
  ((p || "MEDIA").toString().toUpperCase() === "ALTA");
const normalizarFecha = (value: string | string[] | null | undefined) => {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  return raw.toString().slice(0, 10);
};

/* Separa la recomendación del sufijo de ruta que agrega el planificador:
   "…texto… · Parada 2 · 09:30 · ~3.4km"  ->  { texto, parada, hora, km } */
const parseRuta = (comentario?: string) => {
  const c = (comentario || "").trim();
  const m = c.match(/·\s*Parada\s+(\d+)(?:\s*·\s*(\d{1,2}:\d{2}))?(?:\s*·\s*~\s*([\d.]+)\s*km)?\s*$/i);
  if (!m) return { texto: c, parada: undefined as string | undefined, hora: undefined as string | undefined, km: undefined as string | undefined };
  const texto = c.slice(0, m.index).replace(/[·\s]+$/, "").trim();
  return { texto, parada: m[1], hora: m[2], km: m[3] };
};

export default function PlanificacionVisitas() {
  /* Semana visible */
  const [base, setBase] = useState<Date>(monday());
  const dias = useMemo(() => weekdays(base), [base]);

  /* Datos visitas/resumen */
  const [visitas, setVisitas] = useState<IVisita[]>([]);
  const [resumen, setResumen] = useState<IResumenResponse | null>(null);

  /* Roles / identidad */
  const [isJefe, setIsJefe] = useState<boolean>(false);
  const [isGerente, setIsGerente] = useState<boolean>(false);
  const [fiscalCedula, setFiscalCedula] = useState<string>("");
  const [identLoading, setIdentLoading] = useState<boolean>(true);

  /* Lista de fiscales (para Jefe) */
  const [fiscales, setFiscales] = useState<{ cedula: string; nombre: string }[]>([]);
  const [fiscalSeleccionado, setFiscalSeleccionado] = useState<{ cedula: string; nombre: string } | null>(null);

  /* UI */
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({ show: false, msg: "" });

  /* Recomendación IA (una llamada por semana, on-demand) */
  const [recoLoading, setRecoLoading] = useState(false);
  const [reco, setReco] = useState<{ texto: string; ia: boolean } | null>(null);

  /* Mi avance (por PS, on-demand) */
  const [showAvance, setShowAvance] = useState(false);
  const [avance, setAvance] = useState<IAvanceResponse | null>(null);
  const [avanceLoading, setAvanceLoading] = useState(false);

  /* Justificar visita vencida (sin tocar el registro de la visita) */
  const [justificando, setJustificando] = useState<IVisita | null>(null);
  const [justifCausa, setJustifCausa] = useState("");
  const [justifCausaId, setJustifCausaId] = useState<number | null>(null);
  const [justifGuardando, setJustifGuardando] = useState(false);
  const [causas, setCausas] = useState<ICausaIncumplimiento[]>([]);

  /* Modal crear/editar */
  const [modo, setModo] = useState<ModoForm>("crear");
  const [editando, setEditando] = useState<IVisita | null>(null);
  const [showCrear, setShowCrear] = useState(false);
  const [form, setForm] = useState<{ psId?: number; psCodigo?: string; psNombre?: string; fecha?: string; prioridad?: Prioridad; comentario?: string }>({
    prioridad: "MEDIA",
    comentario: "",
  });
  const [psBusqueda, setPsBusqueda] = useState("");
  const [psResultados, setPsResultados] = useState<IfetchPuntoServicio[]>([]);
  const [psBuscando, setPsBuscando] = useState(false);
  const [psErrorBusqueda, setPsErrorBusqueda] = useState<string | null>(null);

  /* Filtros */
  const [filtroEstado, setFiltroEstado] = useState<"TODOS" | "PRIORIDAD" | "PENDIENTE" | "COMPLETADA" | "VENCIDA">("TODOS");
  const totPend = useMemo(() => visitas.filter(v => v.estado === "PENDIENTE").length, [visitas]);
  const totComp = useMemo(() => visitas.filter(v => v.estado === "COMPLETADA").length, [visitas]);
  const totVenc = useMemo(() => visitas.filter(v => v.estado === "VENCIDA").length, [visitas]);
  const totAlta = useMemo(() => visitas.filter(v => esPrioridadAlta(v.prioridad)).length, [visitas]);
  const coincideFiltro = (v: IVisita) => {
    if (filtroEstado === "TODOS") return true;
    if (filtroEstado === "PRIORIDAD") return esPrioridadAlta(v.prioridad);
    return v.estado === filtroEstado;
  };

  /* Extrae cédula desde login.me() en múltiples rutas */
  function extractCedula(obj: any): string | null {
    if (!obj) return null;
    const paths = [
      "emp_cedula","datos.emp_cedula","usuario.emp_cedula","user.emp_cedula",
      "empleado.emp_cedula","persona.emp_cedula","payload.emp_cedula",
      "numCedula","datos.numCedula","cedula","datos.cedula","username","datos.username",
    ];
    const getPath = (o: any, p: string) =>
      p.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), o);
    for (const p of paths) {
      const v = getPath(obj, p);
      if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
    }
    return null;
  }

  const limpiarBusquedaPs = () => {
    setPsBusqueda("");
    setPsResultados([]);
    setPsErrorBusqueda(null);
    setPsBuscando(false);
  };

  const limpiarSeleccionPs = () =>
    setForm(f => ({ ...f, psId: undefined, psCodigo: undefined, psNombre: undefined }));

  const seleccionarPuntoServicio = (ps: IfetchPuntoServicio) => {
    setForm(f => ({
      ...f,
      psId: ps.id,
      psCodigo: ps.ps_codigo ?? (ps.objeto !== undefined ? String(ps.objeto) : undefined),
      psNombre: ps.ps_nombre ?? undefined,
    }));
    limpiarBusquedaPs();
  };

  /* Abrir modal crear/editar */
  const abrirCrear = () => {
    setModo("crear");
    setEditando(null);
    setForm({ prioridad: "MEDIA", comentario: "" });
    limpiarBusquedaPs();
    setShowCrear(true);
  };
  const abrirEditar = (v: IVisita) => {
    setModo("editar");
    setEditando(v);
    setForm({
      psId: v.punto_servicio_id,
      psCodigo: v.ps_objeto || undefined,
      psNombre: v.ps_nombre || undefined,
      fecha: v.fecha,
      prioridad: v.prioridad || "MEDIA",
      comentario: v.comentario || "",
    });
    limpiarBusquedaPs();
    setShowCrear(true);
  };

  /* Identidad + rol + carga de fiscales (Jefe) */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        // 1) Prefetch de cédula desde storage (no bloquea)
        const stored =
          localStorage.getItem("cedula") ||
          localStorage.getItem("numCedula") ||
          sessionStorage.getItem("cedula") ||
          sessionStorage.getItem("numCedula");
        if (stored?.trim() && alive) setFiscalCedula(stored.trim());

        // 2) Obtener usuario y determinar si es Jefe
        const { data } = await login.me();
        if (!alive) return;

        const jefe = esJefeOperaciones(data);
        const gerente = esGerenteOperaciones(data);
        setIsJefe(jefe);
        setIsGerente(gerente);

        // 3) Extraer cédula del usuario logueado (del DWH)
        const ced = extractCedula(data); // asegúrate de tener este helper en el archivo
        if (ced && alive) {
          setFiscalCedula(ced);
          localStorage.setItem("cedula", ced);
        }

        // 4) Si es Jefe: cargar fiscales asignados pasando la cédula del jefe
        if ((jefe || gerente) && (ced || gerente)) {
          try {
            const lista = gerente
              ? await listarFiscalesAsignados()
              : await listarFiscalesAsignados(ced ?? undefined);
            if (!alive) return;
            setFiscales(lista);
            setFiscalSeleccionado(lista[0] ?? null); // preselecciona el primero si existe
          } catch {
            if (alive) setToast({ show: true, msg: "No se pudieron listar los fiscales", color: "danger" });
          }
        } else {
          // No es Jefe: limpiar selector de fiscales
          setFiscales([]);
          setFiscalSeleccionado(null);
        }
      } catch (e: any) {
        if (alive) setToast({ show: true, msg: e?.message || "No se pudo obtener el usuario", color: "danger" });
      } finally {
        if (alive) setIdentLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let cancelado = false;
    const termino = psBusqueda.trim();

    if (!showCrear || termino.length < 3) {
      if (!cancelado) {
        setPsResultados([]);
        setPsErrorBusqueda(null);
        setPsBuscando(false);
      }
      return;
    }

    setPsBuscando(true);
    (async () => {
      try {
        const resultado = await fetchPuntoServicio(termino);
        if (cancelado) return;
        if (resultado.error) {
          setPsResultados([]);
          setPsErrorBusqueda(resultado.error);
        } else {
          setPsResultados(resultado.datos || []);
          setPsErrorBusqueda(null);
        }
      } catch {
        if (!cancelado) {
          setPsResultados([]);
          setPsErrorBusqueda("No se pudo buscar el Punto de Servicio");
        }
      } finally {
        if (!cancelado) setPsBuscando(false);
      }
    })();

    return () => { cancelado = true; };
  }, [psBusqueda, showCrear]);

  useEffect(() => {
    if (!showCrear) {
      setPsBusqueda("");
      setPsResultados([]);
      setPsErrorBusqueda(null);
      setPsBuscando(false);
    }
  }, [showCrear]);


  /* Cedula efectiva a consultar (Jefe usa la del fiscal elegido) */
  const puedeVerFiscales = isJefe || isGerente;
  const puedeGestionar = isJefe || isGerente;
  const cedulaParaConsultar = puedeVerFiscales
    ? (fiscalSeleccionado?.cedula || "")
    : fiscalCedula;

  /* Cargar semana + resumen */
  const cargar = async () => {
    if (!cedulaParaConsultar) return;
    setLoading(true);
    setReco(null); // la recomendación previa deja de ser válida al cambiar semana/fiscal
    try {
      const desde = fmtISO(dias[0]);
      const hasta = fmtISO(dias[dias.length - 1]);
      const lista = await listarVisitas({ desde, hasta, fiscal_id: cedulaParaConsultar });
      setVisitas(lista);

      const mid = new Date(base);
      const r = await resumenVisitas(cedulaParaConsultar, yearOf(mid), monthOf(mid));
      setResumen(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!identLoading) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, identLoading, cedulaParaConsultar]);

  /* Navegación semana: el FISCAL puede volver a semanas PASADAS (para revisar y
     justificar vencidas) pero no adelantarse a la semana actual (el plan se genera
     para todo el mes pero se le muestra de a una semana); jefe/gerente navegan libre. */
  const puedeNavegarSemanas = isJefe || isGerente;
  const semanaActual = monday();
  const puedeIrAdelante = puedeNavegarSemanas || base.getTime() < semanaActual.getTime();
  const onPrev = () => setBase(monday(new Date(base.getTime() - 7 * 86400000)));
  const onNext = () => setBase(monday(new Date(base.getTime() + 7 * 86400000)));

  /* Justificar visita vencida */
  const abrirJustificar = async (v: IVisita) => {
    setJustificando(v);
    setJustifCausa(v.justificacion?.causa || "");
    setJustifCausaId(v.justificacion?.causa_id ?? null);
    try {
      setCausas(await listarCausasIncumplimiento());
    } catch {
      setCausas([]); // sin catálogo: texto libre
    }
  };
  const onJustificar = async () => {
    if (!justificando) return;
    const causaTxt = justifCausa.trim()
      || causas.find(c => c.id === justifCausaId)?.descripcion
      || "";
    if (!causaTxt) {
      setToast({ show: true, msg: "Indica la causa del incumplimiento", color: "warning" });
      return;
    }
    setJustifGuardando(true);
    try {
      await justificarVisita(justificando.id, causaTxt, justifCausaId);
      setToast({ show: true, msg: "Justificación registrada", color: "success" });
      setJustificando(null);
      setJustifCausa("");
      setJustifCausaId(null);
      await cargar();
    } catch (e: any) {
      setToast({ show: true, msg: e?.response?.data?.error || e?.message || "No se pudo justificar", color: "danger" });
    } finally {
      setJustifGuardando(false);
    }
  };

  const fmtCorta = (iso?: string | null) => {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };

  /* Guardar (crear/editar) */
  const onGuardar = async () => {
    if (!cedulaParaConsultar) {
      setToast({ show: true, msg: "No se encontró la cédula del fiscal.", color: "warning" });
      return;
    }
    if (!form.fecha || !form.psId) {
      setToast({ show: true, msg: "Selecciona Punto de Servicio y fecha", color: "warning" });
      return;
    }

    try {
      if (modo === "crear") {
        await crearVisita({
          fiscal_id: cedulaParaConsultar,
          punto_servicio_id: form.psId,
          ps_codigo: form.psCodigo,
          fecha: form.fecha!,
          prioridad: form.prioridad || "MEDIA",
          comentario: form.comentario || undefined,
        });
        setToast({ show: true, msg: "Visita creada", color: "success" });
      } else if (modo === "editar" && editando) {
        await editarVisita({
          id: editando.id,
          fecha: form.fecha!,
          punto_servicio_id: form.psId,
          ps_codigo: form.psCodigo,
          prioridad: form.prioridad || "MEDIA",
          comentario: form.comentario || undefined,
        });
        setToast({ show: true, msg: "Visita actualizada", color: "success" });
      }

      setShowCrear(false);
      setForm({ prioridad: "MEDIA", comentario: "" });
      await cargar();
    } catch (e: any) {
      setToast({ show: true, msg: e?.message || "No se pudo guardar", color: "danger" });
    }
  };

  /* Generar recomendación IA de la semana visible (una sola llamada) */
  const generarRecomendacion = async () => {
    if (!cedulaParaConsultar) {
      setToast({ show: true, msg: "No se encontró la cédula del fiscal.", color: "warning" });
      return;
    }
    setRecoLoading(true);
    try {
      const r = await recomendacionRuta(cedulaParaConsultar, fmtISO(dias[0]), fmtISO(dias[dias.length - 1]));
      setReco({ texto: r.recomendacion, ia: r.ia });
    } catch (e: any) {
      setToast({ show: true, msg: e?.message || "No se pudo generar la recomendación", color: "danger" });
    } finally {
      setRecoLoading(false);
    }
  };

  /* Abrir "Mi avance": carga el detalle por PS del mes visible */
  const abrirAvance = async () => {
    if (!cedulaParaConsultar) {
      setToast({ show: true, msg: "No se encontró la cédula del fiscal.", color: "warning" });
      return;
    }
    setShowAvance(true);
    setAvanceLoading(true);
    try {
      const mid = new Date(base);
      const a = await avanceFiscal(cedulaParaConsultar, yearOf(mid), monthOf(mid));
      setAvance(a);
    } catch (e: any) {
      setToast({ show: true, msg: e?.message || "No se pudo cargar el avance", color: "danger" });
      setShowAvance(false);
    } finally {
      setAvanceLoading(false);
    }
  };

  /* Mensaje motivacional según % de visitas (2ª persona para el fiscal, neutro para el jefe) */
  const mensajeAvance = (pct: number | null | undefined, vistaJefe: boolean) => {
    const p = pct ?? 0;
    if (vistaJefe) {
      const nombre = fiscalSeleccionado?.nombre || "El fiscal";
      if (p >= 100) return `🎉 ${nombre} completó todas sus visitas del mes.`;
      if (p >= 75) return `💪 ${nombre} lleva el ${p}% del mes — buen ritmo.`;
      if (p >= 50) return `👏 ${nombre} lleva el ${p}% del mes.`;
      if (p >= 25) return `🚀 ${nombre} lleva el ${p}% del mes.`;
      return `📅 ${nombre} lleva el ${p}% del mes. Abajo se ven los puntos con más pendientes.`;
    }
    if (p >= 100) return "🎉 ¡Felicitaciones! Completaste todas tus visitas del mes.";
    if (p >= 75) return `💪 ¡Excelente ritmo! Ya vas por el ${p}% — falta poco.`;
    if (p >= 50) return `👏 ¡Buen avance! Llevás el ${p}% del mes. Seguí así.`;
    if (p >= 25) return `🚀 Vas por el ${p}%. ¡A seguir sumando visitas!`;
    return `📅 Llevás el ${p}% del mes. Mirá abajo qué puntos te faltan y planificá tu ruta.`;
  };

  const rangoTitulo = `${fmtISO(dias[0])} – ${fmtISO(dias[dias.length - 1])}`;

  return (
    <IonPage>
      <Header titulo="Planificación de Visitas" />
      <IonHeader className="sticky-bar">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onPrev}><IonIcon icon={chevronBack} /></IonButton>
          </IonButtons>
            <IonToolbar style={ { textAlign: "center", width: "100%" } }>
              <IonTitle size="small">{rangoTitulo}</IonTitle>
              {resumen && (
                <div className="px-4 pb-2" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
                  <IonBadge color="success">
                    Visitas {resumen.visitas?.realizadas ?? resumen.realizadas}/{resumen.visitas?.meta ?? resumen.total}
                    {resumen.visitas?.pct != null ? ` · ${resumen.visitas.pct}%` : ""}
                  </IonBadge>
                  {resumen.horas && (
                    <IonBadge color="tertiary">
                      Horas {resumen.horas.realizadas_hhmm}/{resumen.horas.meta_hhmm}
                      {resumen.horas.pct != null ? ` · ${resumen.horas.pct}%` : ""}
                    </IonBadge>
                  )}
                  {resumen.cumplimiento?.pct != null && (
                    <IonBadge color={colorCumplimiento(resumen.cumplimiento.pct)}>
                      Cumplimiento {resumen.cumplimiento.pct}%
                    </IonBadge>
                  )}
                  <IonButton size="small" fill="clear" onClick={abrirAvance}>
                    <IonIcon slot="start" icon={trophy} />
                    {puedeVerFiscales ? "Avance del fiscal" : "Mi avance"}
                  </IonButton>
                </div>
              )}
            </IonToolbar>
          <IonButtons slot="end">
            {puedeGestionar && fiscalSeleccionado && (
              <IonButton onClick={abrirCrear}>
                <IonIcon slot="start" icon={add} />
                Crear
              </IonButton>
            )}
            {puedeIrAdelante && (
              <IonButton onClick={onNext}><IonIcon icon={chevronForward} /></IonButton>
            )}
          </IonButtons>
        </IonToolbar>



        {/* Selector de Fiscal (solo Jefe) */}
        {(puedeVerFiscales || fiscales.length > 0) && (
          <IonToolbar>
            <div className="px-4 pb-2">
              <IonItem lines="none">
                <IonLabel position="stacked">Fiscal</IonLabel>
                <IonSelect
                  interface="alert"
                  placeholder="Selecciona un fiscal"
                  value={fiscalSeleccionado?.cedula || ""}
                  onIonChange={(e) => {
                    const ced = (e.detail.value || "").toString();
                    const f = fiscales.find(x => x.cedula === ced) || null;
                    setFiscalSeleccionado(f);
                  }}
                >
                  {fiscales.map(f => (
                    <IonSelectOption key={f.cedula} value={f.cedula}>
                      {f.nombre} — {f.cedula}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </div>
          </IonToolbar>
        )}

        {/* Filtro por estado */}
        <IonToolbar>
          <div className="px-4 pb-2">
              <IonSegment
              value={filtroEstado}
              scrollable
              onIonChange={(e) => setFiltroEstado((e.detail.value as any) || "TODOS")}
            >
              <IonSegmentButton value="TODOS">Todos</IonSegmentButton>
              <IonSegmentButton value="PRIORIDAD">Prioridad alta ({totAlta})</IonSegmentButton>
              <IonSegmentButton value="VENCIDA">Vencidas ({totVenc})</IonSegmentButton>
              <IonSegmentButton value="PENDIENTE">Pendientes ({totPend})</IonSegmentButton>
              <IonSegmentButton value="COMPLETADA">Completadas ({totComp})</IonSegmentButton>
            </IonSegment>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        {(identLoading || loading) && (
          <div className="ion-padding"><IonSpinner /></div>
        )}

        {/* Recomendación IA de la semana (on-demand) */}
        {cedulaParaConsultar && (
          <div className="ion-padding-horizontal" style={{ paddingTop: 8 }}>
            <IonButton
              expand="block"
              fill="outline"
              disabled={recoLoading}
              onClick={generarRecomendacion}
            >
              {recoLoading ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon slot="start" icon={sparkles} />
                  Recomendación IA del mes
                </>
              )}
            </IonButton>
            {reco && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: 16 }}>
                    Recomendación {reco.ia ? "IA" : "(resumen)"}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div style={{ whiteSpace: "pre-line" }}>{reco.texto}</div>
                </IonCardContent>
              </IonCard>
            )}
          </div>
        )}

        {/* Lista vertical por días (móvil y desktop) */}
        <div className="days-vertical">
          {dias.map((d) => {
            const iso = fmtISO(d);
            const items = visitas
              .filter(v => v.fecha === iso && coincideFiltro(v))
              .sort((a, b) => a.id - b.id); // orden de ruta sugerida (orden de generación)

            return (
              <IonCard key={iso} className="day-card">
                <IonCardHeader>
                  <IonCardTitle>{iso}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none" className="visits-list">
                    {items.length === 0 && (
                      <IonItem className="visit-row">
                        <IonLabel color="medium">Sin visitas</IonLabel>
                      </IonItem>
                    )}

                    {items.map(v => {
                      const objeto = v.ps_objeto?.trim() || "";
                      const nombre = v.ps_nombre?.trim() || "";
                      const tienePS = !!(objeto || nombre);
                      const fallback = `PS #${v.punto_servicio_id}`;
                      const titleTooltip = tienePS
                        ? `${objeto}${objeto && nombre ? " — " : ""}${nombre}`
                        : fallback;
                      const ruta = parseRuta(v.comentario);

                      return (
                        <IonItem key={v.id} className="visit-row">
                          <IonLabel>
                            <div className="visit-header">
                              <div className="ps-title" title={titleTooltip}>
                                {tienePS ? (
                                  <>
                                    <div className="ps-objeto">{objeto}</div>
                                    {nombre && <div className="ps-nombre">{nombre}</div>}
                                  </>
                                ) : (
                                  <div className="ps-objeto">{fallback}</div>
                                )}
                              </div>
                              <IonBadge className="state-badge" color={estadoColor(v.estado)}>{v.estado}</IonBadge>
                            </div>
                            <div className="visit-meta">
                              {ruta.parada && (
                                <IonBadge color="primary" style={{ marginRight: 8 }}>
                                  Parada {ruta.parada}
                                </IonBadge>
                              )}
                              <IonBadge color={prioridadColor(v.prioridad)} style={{ marginRight: 8 }}>
                                {v.prioridad || "MEDIA"}
                              </IonBadge>
                              {ruta.hora && (
                                <IonBadge color="tertiary" style={{ marginRight: 8 }}>
                                  {ruta.hora}
                                </IonBadge>
                              )}
                              {ruta.km && (
                                <span style={{ marginRight: 8, color: "var(--ion-color-medium)" }}>
                                  ~{ruta.km} km
                                </span>
                              )}
                              <span>{v.origen}</span>
                            </div>
                            {v.estado === "COMPLETADA" && textoCumplimientoVisita(v) && (
                              <div style={{ marginTop: 6 }}>
                                <IonBadge color={colorCumplimiento(v.pct_cumplimiento)}>
                                  {textoCumplimientoVisita(v)}
                                </IonBadge>
                              </div>
                            )}
                            {!!ruta.texto && (
                              <div className="visit-comment">
                                <strong>Recomendación:</strong> {ruta.texto}
                              </div>
                            )}
                            {v.estado === "VENCIDA" && (
                              <div style={{ marginTop: 6 }}>
                                {v.fecha_reprogramacion && (
                                  <IonBadge color="tertiary" style={{ marginRight: 8 }}>
                                    Reprogramada: {fmtCorta(v.fecha_reprogramacion)}
                                  </IonBadge>
                                )}
                                {v.justificacion ? (
                                  <div className="visit-comment" style={{ marginTop: 4 }}>
                                    <strong>Justificada:</strong> {v.justificacion.causa}
                                    {v.justificacion.por_nombre ? ` (${v.justificacion.por_nombre})` : ""}
                                  </div>
                                ) : (
                                  <IonButton size="small" fill="outline" color="danger" onClick={() => abrirJustificar(v)}>
                                    Justificar
                                  </IonButton>
                                )}
                              </div>
                            )}
                          </IonLabel>

                          {puedeGestionar && (
                            <div className="visit-actions">
                              <IonButton size="small" fill="clear" onClick={() => abrirEditar(v)} title="Editar visita">
                                <IonIcon icon={pencil} />
                              </IonButton>
                            </div>
                          )}
                        </IonItem>
                      );
                    })}
                  </IonList>
                </IonCardContent>
              </IonCard>
            );
          })}
        </div>

        {/* Modal Mi avance: cumplimiento por PS para incentivar las visitas */}
        <IonModal isOpen={showAvance} onDidDismiss={() => setShowAvance(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {puedeVerFiscales
                  ? `Avance de ${fiscalSeleccionado?.nombre || "fiscal"}`
                  : "Mi avance del mes"}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAvance(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {avanceLoading && <div className="ion-padding ion-text-center"><IonSpinner /></div>}
            {!avanceLoading && avance && (
              <>
                <IonCard>
                  <IonCardContent>
                    <div style={{ fontSize: 15, marginBottom: 10 }}>
                      {mensajeAvance(avance.totales.cumplimiento?.pct ?? avance.totales.visitas.pct, puedeVerFiscales)}
                    </div>
                    {avance.totales.cumplimiento?.pct != null && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                          <strong>Cumplimiento (promedio de visitas y horas)</strong>
                          <strong>{avance.totales.cumplimiento.pct}%</strong>
                        </div>
                        <IonProgressBar color="primary" value={avance.totales.cumplimiento.pct / 100} />
                      </div>
                    )}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span>Visitas {avance.totales.visitas.realizadas}/{avance.totales.visitas.meta}</span>
                        <strong>{avance.totales.visitas.pct ?? 0}%</strong>
                      </div>
                      <IonProgressBar color="success" value={(avance.totales.visitas.pct ?? 0) / 100} />
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span>Horas {avance.totales.horas.realizadas_hhmm}/{avance.totales.horas.meta_hhmm}</span>
                        <strong>{avance.totales.horas.pct ?? 0}%</strong>
                      </div>
                      <IonProgressBar color="tertiary" value={(avance.totales.horas.pct ?? 0) / 100} />
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--ion-color-medium)" }}>
                      {avance.totales.ps_completos}/{avance.totales.ps_asignados} puntos de servicio completos
                    </div>
                  </IonCardContent>
                </IonCard>

                <IonList>
                  {avance.detalle.map(d => (
                    <IonItem key={d.ps_id} lines="full">
                      <IonLabel className="ion-text-wrap">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <strong style={{ fontSize: 14 }}>{d.ps_nombre || d.ps_codigo || `PS #${d.ps_id}`}</strong>
                          {d.completo
                            ? <IonIcon icon={checkmarkCircle} color="success" />
                            : <IonBadge color={d.visitas.faltan > 0 ? "danger" : "warning"}>
                                {d.visitas.faltan > 0 ? `Faltan ${d.visitas.faltan} visita${d.visitas.faltan > 1 ? "s" : ""}` : `Faltan ${d.horas.faltan_hhmm} hs`}
                              </IonBadge>}
                        </div>
                        {d.cumplimiento?.pct != null && (
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            Cumplimiento <strong>{d.cumplimiento.pct}%</strong>
                          </div>
                        )}
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Visitas {d.visitas.realizadas}/{d.visitas.meta}
                        </div>
                        <IonProgressBar color="success" value={(d.visitas.pct ?? 0) / 100} />
                        {d.horas.pct != null && (
                          <>
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              Horas {d.horas.realizadas_hhmm}/{d.horas.meta_hhmm}
                            </div>
                            <IonProgressBar color="tertiary" value={(d.horas.pct ?? 0) / 100} />
                          </>
                        )}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </IonContent>
        </IonModal>

        {/* Modal justificar visita vencida (no modifica el registro de la visita) */}
        <IonModal isOpen={!!justificando} onDidDismiss={() => setJustificando(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Justificar visita</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setJustificando(null)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {justificando && (
              <IonList>
                <IonItem lines="full">
                  <IonLabel className="ion-text-wrap">
                    <strong>{justificando.ps_nombre || justificando.ps_objeto || `PS #${justificando.punto_servicio_id}`}</strong>
                    <div style={{ fontSize: 13 }}>Programada para el {fmtCorta(justificando.fecha)}</div>
                    {justificando.fecha_reprogramacion && (
                      <div style={{ fontSize: 13 }}>
                        Reprogramada para el {fmtCorta(justificando.fecha_reprogramacion)}
                      </div>
                    )}
                  </IonLabel>
                </IonItem>

                {causas.length > 0 && (
                  <IonItem>
                    <IonLabel position="stacked">Causa (catálogo)</IonLabel>
                    <IonSelect
                      interface="alert"
                      interfaceOptions={{ cssClass: "alert-causas-incumplimiento" }}
                      placeholder="Selecciona una causa"
                      value={justifCausaId ?? undefined}
                      onIonChange={(e) => setJustifCausaId(e.detail.value ?? null)}
                    >
                      {causas.map(c => (
                        <IonSelectOption key={c.id} value={c.id}>{c.descripcion}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                )}

                <IonItem>
                  <IonLabel position="stacked">
                    {causas.length > 0 ? "Detalle (opcional si eliges del catálogo)" : "Causa del incumplimiento"}
                  </IonLabel>
                  <IonTextarea
                    autoGrow
                    placeholder="Explica por qué no se pudo realizar la visita"
                    value={justifCausa}
                    onIonChange={(e) => setJustifCausa(e.detail.value || "")}
                  />
                </IonItem>
              </IonList>
            )}
            <div className="ion-padding">
              <IonButton expand="block" disabled={justifGuardando} onClick={onJustificar}>
                {justifGuardando ? <IonSpinner name="dots" /> : "Registrar justificación"}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Modal crear/editar visita */}
        <IonModal isOpen={showCrear} onDidDismiss={() => setShowCrear(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{modo === "crear" ? "Crear visita" : "Editar visita"}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowCrear(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent className="ion-padding">
            <IonList>
              <IonItem lines="full">
                <IonLabel className="ion-text-wrap">
                  <strong>Punto de Servicio seleccionado</strong>
                  {form.psId ? (
                    <>
                      <div>{form.psNombre || "Sin nombre"}</div>
                      <div className="ps-objeto">Código/ID: {form.psCodigo || form.psId}</div>
                    </>
                  ) : (
                    <div>Selecciona un punto de servicio usando la búsqueda.</div>
                  )}
                </IonLabel>
                {form.psId && (
                  <IonButton slot="end" fill="clear" onClick={limpiarSeleccionPs}>
                    Limpiar
                  </IonButton>
                )}
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Buscar Punto de Servicio</IonLabel>
                <IonInput
                  clearInput
                  value={psBusqueda}
                  placeholder="Ingresa al menos 3 letras"
                  onIonChange={(e) => setPsBusqueda((e.detail.value ?? "").toString())}
                />
              </IonItem>

              <IonItem lines="none">
                <IonLabel className="ion-text-wrap">
                  {psErrorBusqueda ? (
                    <IonText color="danger">{psErrorBusqueda}</IonText>
                  ) : psBusqueda.trim().length === 0 ? (
                    <IonText color="medium">Ingresa al menos 3 letras para iniciar la búsqueda.</IonText>
                  ) : psBusqueda.trim().length < 3 ? (
                    <IonText color="medium">Escribe {3 - psBusqueda.trim().length} letra(s) más para buscar.</IonText>
                  ) : psResultados.length === 0 && !psBuscando ? (
                    <IonText color="warning">No se encontraron coincidencias para "{psBusqueda}".</IonText>
                  ) : (
                    <IonText color="medium">Selecciona un resultado de la lista.</IonText>
                  )}
                </IonLabel>
                {psBuscando && <IonSpinner slot="end" />}
              </IonItem>

              {psResultados.length > 0 && psResultados.map((ps) => (
                <IonItem
                  key={ps.id}
                  button
                  detail={false}
                  onClick={() => seleccionarPuntoServicio(ps)}
                  color={form.psId === ps.id ? "light" : undefined}
                >
                  <IonLabel>
                    <strong>{ps.ps_nombre || "Sin nombre"}</strong>
                    <div>{ps.ps_codigo || ps.objeto}</div>
                  </IonLabel>
                  {form.psId === ps.id && <IonIcon slot="end" icon={checkmarkCircle} color="success" />}
                </IonItem>
              ))}

              <IonItem>
                <IonLabel position="stacked">Fecha</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  value={form.fecha ?? undefined}
                  onIonChange={(e) =>
                    setForm(f => ({ ...f, fecha: normalizarFecha(e.detail.value) }))
                  }
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Prioridad</IonLabel>
                <IonSelect
                  interface="alert"
                  value={form.prioridad ?? "MEDIA"}
                  onIonChange={(e) =>
                    setForm(f => ({ ...f, prioridad: (e.detail.value as Prioridad) || "MEDIA" }))
                  }
                >
                  <IonSelectOption value="ALTA">ALTA</IonSelectOption>
                  <IonSelectOption value="MEDIA">MEDIA</IonSelectOption>
                  <IonSelectOption value="BAJA">BAJA</IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Comentario (opcional)</IonLabel>
                <IonTextarea
                  autoGrow
                  placeholder="Notas para el fiscal"
                  value={form.comentario || ""}
                  onIonChange={(e) => setForm(f => ({ ...f, comentario: e.detail.value || "" }))}
                />
              </IonItem>
            </IonList>

            <div className="ion-padding">
              <IonButton expand="block" onClick={onGuardar}>
                {modo === "crear" ? "Crear" : "Guardar cambios"}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={2200}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
}
